import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, orderBy, query, where, type Unsubscribe } from 'firebase/firestore';
import { functions, db, getRealTimeActiveAuthenticatedUsersCount } from '../../config/data/firebase';
import type { ChatConfig, ChatMessage, ChatThread } from '../engine/types';

type Bootstrap = {
	enabled: boolean;
	welcomeMessage: string;
	offlineHumanNotice: string;
	staffOnline: boolean;
};

const asDate = (value: { toDate?: () => Date } | undefined): Date | undefined => value?.toDate?.();

const mapThread = (id: string, data: Record<string, unknown>): ChatThread => ({
	...(data as Omit<ChatThread, 'id' | 'lastMessageAt'>),
	id,
	lastMessageAt: asDate(data.lastMessageAt as { toDate?: () => Date }),
	deletedAt: asDate(data.deletedAt as { toDate?: () => Date }),
});

const mapMessage = (id: string, data: Record<string, unknown>): ChatMessage => ({
	...(data as Omit<ChatMessage, 'id' | 'createdAt'>),
	id,
	createdAt: asDate(data.createdAt as { toDate?: () => Date }),
});

export const getChatBootstrap = async (): Promise<Bootstrap> => (await httpsCallable<void, Bootstrap>(functions, 'getChatBootstrap')()).data;

export const sendChatMessage = async (body: string, threadId?: string): Promise<{ threadId: string }> => {
	const response = await httpsCallable<{ body: string; threadId?: string }, { threadId: string }>(functions, 'sendChatMessage')({ body, threadId });
	return response.data;
};

export const dismissChatThread = async (threadId: string): Promise<void> => {
	await httpsCallable<{ threadId: string }, { success: boolean }>(functions, 'dismissChatThread')({ threadId });
};

export const deleteChatThread = async (threadId: string): Promise<void> => {
	await httpsCallable<{ threadId: string }, { success: boolean }>(functions, 'deleteChatThread')({ threadId });
};

export const openChatThread = async (participantIds: string[]): Promise<{ threadId: string }> =>
	(await httpsCallable<{ participantIds: string[] }, { threadId: string }>(functions, 'openChatThread')({ participantIds })).data;

export interface ChatPerson {
	uid: string;
	role: 'applicant' | 'member';
	displayName: string;
	email: string;
}

export const searchChatPeople = async (search: string): Promise<ChatPerson[]> =>
	(await httpsCallable<{ query: string }, ChatPerson[]>(functions, 'searchChatPeople')({ query: search })).data;

export const prepareChatAccountTransition = async (): Promise<string> =>
	(await httpsCallable<void, { token: string }>(functions, 'prepareChatAccountTransition')()).data.token;

export const claimChatAccountTransition = async (token: string): Promise<void> => {
	await httpsCallable<{ token: string }, { success: boolean }>(functions, 'claimChatAccountTransition')({ token });
};

export const getChatAdminConfig = async (): Promise<ChatConfig> => (await httpsCallable<void, ChatConfig>(functions, 'getChatAdminConfig')()).data;

export const saveChatAdminConfig = async (config: Partial<ChatConfig>): Promise<void> => {
	await httpsCallable<Partial<ChatConfig>, { success: boolean }>(functions, 'saveChatAdminConfig')(config);
};

export const subscribeToMessages = (threadId: string, callback: (messages: ChatMessage[]) => void): Unsubscribe =>
	onSnapshot(query(collection(db, 'chatThreads', threadId, 'messages'), orderBy('createdAt', 'asc')), (snapshot) => {
		callback(snapshot.docs.map((document) => mapMessage(document.id, document.data())));
	});

export const subscribeToParticipantThreads = (uid: string, callback: (threads: ChatThread[]) => void): Unsubscribe =>
	onSnapshot(query(collection(db, 'chatThreads'), where('participantIds', 'array-contains', uid)), (snapshot) => {
		callback(snapshot.docs
			.map((document) => mapThread(document.id, document.data()))
			.filter((thread) => !thread.deletedAt && !thread.hiddenForIds?.includes(uid))
			.sort((a, b) => (b.lastMessageAt?.getTime() || 0) - (a.lastMessageAt?.getTime() || 0)));
	});

export const subscribeToQueuedThreads = (uid: string, callback: (threads: ChatThread[]) => void): Unsubscribe =>
	onSnapshot(query(collection(db, 'chatThreads'), where('queueMemberIds', 'array-contains', uid)), (snapshot) => {
		callback(snapshot.docs
			.map((document) => mapThread(document.id, document.data()))
			.filter((thread) => !thread.deletedAt && !thread.hiddenForIds?.includes(uid))
			.sort((a, b) => (b.lastMessageAt?.getTime() || 0) - (a.lastMessageAt?.getTime() || 0)));
	});

export const subscribeToAllThreads = (callback: (threads: ChatThread[]) => void): Unsubscribe =>
	onSnapshot(collection(db, 'chatThreads'), (snapshot) => {
		callback(snapshot.docs
			.map((document) => mapThread(document.id, document.data()))
			.filter((thread) => !thread.deletedAt)
			.sort((a, b) => (b.lastMessageAt?.getTime() || 0) - (a.lastMessageAt?.getTime() || 0)));
	});

/** Same active-window definition as the member dashboard LIVE COUNT widget. */
export const subscribeToPresence = (callback: (online: number) => void): (() => void) =>
	getRealTimeActiveAuthenticatedUsersCount(callback) || (() => {});
